import { AlertTriangle, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { SemAcesso } from '@/components/layout/SemAcesso';
import { Badge, Skeleton } from '@/components/ui/basicos';
import { eErroDaApi, mensagemPara } from '@/lib/api/erros';
import { cn } from '@/lib/utils';

/* Os quatro estados de toda tela (constituição IV.2): carregando, com dados, vazio e erro. */

interface EstadoVazioProps {
  titulo: string;
  descricao?: string | undefined;
  /** Ex.: o botão "Novo exercício". */
  acao?: ReactNode | undefined;
}

export function EstadoVazio({ titulo, descricao, acao }: EstadoVazioProps) {
  return (
    <div className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <Inbox className="size-10 text-muted-foreground" aria-hidden />
      <p className="font-medium">{titulo}</p>
      {descricao !== undefined && <p className="text-sm text-muted-foreground">{descricao}</p>}
      {acao}
    </div>
  );
}

interface EstadoDeErroProps {
  erro: unknown;
  aoTentarDeNovo?: () => void;
}

/**
 * Mensagem humana + `requestId` em letra pequena, para o suporte achar o log (docs/02). Um 403
 * vira "Você não tem acesso a esta tela." com o motivo (spec 003, CA-03).
 */
export function EstadoDeErro({ erro, aoTentarDeNovo }: EstadoDeErroProps) {
  if (eErroDaApi(erro) && erro.status === 403) return <SemAcesso motivo={erro.motivo} />;
  const requestId = eErroDaApi(erro) ? erro.requestId : undefined;
  return (
    <div role="alert" className="flex flex-col items-center gap-3 px-4 py-12 text-center">
      <AlertTriangle className="size-10 text-destructive" aria-hidden />
      <p className="font-medium">{mensagemPara(erro)}</p>
      {requestId !== undefined && (
        <p className="font-mono text-xs text-muted-foreground">Código: {requestId}</p>
      )}
      {aoTentarDeNovo !== undefined && (
        <Button variant="outline" onClick={aoTentarDeNovo}>
          Tentar de novo
        </Button>
      )}
    </div>
  );
}

export function Esqueleto({ linhas = 5, className }: { linhas?: number; className?: string }) {
  return (
    <div
      role="status"
      className={cn('flex flex-col gap-3', className)}
      aria-busy="true"
      aria-label="Carregando"
    >
      {Array.from({ length: linhas }, (_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

interface CabecalhoDaPaginaProps {
  titulo: string;
  descricao?: string | undefined;
  /** Ação principal, ex. "Novo exercício". */
  acao?: ReactNode;
}

export function CabecalhoDaPagina({ titulo, descricao, acao }: CabecalhoDaPaginaProps) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold tracking-tight">{titulo}</h1>
        {descricao !== undefined && <p className="text-sm text-subtitulo">{descricao}</p>}
      </div>
      {acao}
    </header>
  );
}

/**
 * Funcionalidade do roadmap (ADR-0016): aparece onde vai ficar, com "Em breve" e sem ação.
 * Anunciada como "<rótulo>, em breve".
 */
export function EmBreve({ rotulo, className }: { rotulo: string; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex cursor-default items-center gap-2 text-muted-foreground',
        className,
      )}
      aria-label={`${rotulo}, em breve`}
      role="note"
    >
      <span aria-hidden>{rotulo}</span>
      <Badge variant="secondary" aria-hidden>
        Em breve
      </Badge>
    </span>
  );
}
